import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { Percentage } from '@ui/components/Amount/Percentage';
import { Star, StarOutline } from '@ui/components/icons';
import { StatAmountUsd } from '@ui/components/Stats';
import { TextTooltip } from '@ui/components/TextTooltip';
import { FUNDING_RATE_DISPLAY_DECIMALS } from '@ui/constants/numbersDecimals';
import { getPairMaxLeverage } from '@ui/features/MassiveToken/utils/getPairMaxLeverage';
import { MarketTimeIndicator } from '@ui/features/RWA/components/MarketTimeIndicator';
import { useIsPairClose } from '@ui/features/RWA/hooks/useIsPairClose';
import { SleepIndicator } from '@ui/features/RWA/SleepIndicator';
import { LeverageBadge } from '@ui/features/Trade/components/LeverageBadge';
import { useLongShortFundingRatePerHour } from '@ui/features/Trade/hooks/useBorrowRates';
import { useSetTradingPair } from '@ui/features/Trade/store/useSetTradingPair';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useIsTradePairSupportDegen } from '@ui/hooks/trade/useIsTradePairSupportDegen';
import { useOpenInterest } from '@ui/hooks/trade/useOpenInterest';
import { useIntersectionObserver } from '@ui/hooks/useIntersectionObserver';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { Badge } from '@ui/lib/park/ui/badge';
import { Button } from '@ui/lib/park/ui/button';
import { Icon } from '@ui/lib/park/ui/icon';
import { Table } from '@ui/lib/park/ui/table';
import { Text } from '@ui/lib/park/ui/text';
import { GetVolumeRangeEnum } from '@ui/services/backend/generated';
import {
  useBaseTradePairsVolume,
  useTradePairsVolumeByAddresses,
  useTradePairVolume,
} from '@ui/services/backend/hooks/useTradePairVolume';
import { usePair24hAgoPrice } from '@ui/services/backend/hooks/oracle/usePair24hAgoPrice';
import { TradePair } from '@ui/types/tradePair';
import { getLocalStorageKey } from '@ui/utils/getLocalStorageKey';
import { getSemanticPropsByNumber } from '@ui/utils/getSemanticProps';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';
import { css } from 'styled-system/css';
import { Box, Flex, HStack, styled, VStack } from 'styled-system/jsx';

import { useTradePairsShowing } from '../../hooks/useTradePairsShowing';
import { AlphaMarketIntroModal } from './AlphaMarketIntroModal';
import { useTradePairWatchListStore } from './hooks/useTradePairWatchListStore';
import { MassiveTokenHead } from './MassiveTokenHead';

const getAlphaMarketIntroStorageKey = () =>
  getLocalStorageKey('massive-token.alpha-market-intro');

const hasSeenAlphaMarketIntro = () => {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(getAlphaMarketIntroStorageKey()) === '1';
  } catch {
    return true;
  }
};

const markAlphaMarketIntroSeen = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(getAlphaMarketIntroStorageKey(), '1');
  } catch {
    // ignore
  }
};

export const MassiveTokenSelectTable = ({
  onItemClickCallBack,
}: {
  onItemClickCallBack?: () => void;
}) => {
  const { t } = useTranslation();

  const sortedTradingPairs = useTradePairsShowing();
  const setTradingPair = useSetTradingPair();
  const [isAlphaIntroOpen, setIsAlphaIntroOpen] = useState(false);
  const pendingItemClickCbRef = useRef<null | (() => void)>(null);

  const currentWatchList = useTradePairWatchListStore((s) =>
    s.useCurrentWatchList(),
  );

  const currentTradePairInfo = useTradingPair((s) => s.useTradingPair());

  return (
    <Flex
      w="full"
      flexDir="column"
      alignItems="center"
      flex={1}
      // bgColor="black.lightBlack"
      overflow="auto"
    >
      <Box w="full">
        <Table.Root variant="massiveTokenTable" position="relative" flex={1}>
          <MassiveTokenHead />
          <Table.Body overflow="auto" w="full" flex={1} minH={0}>
            {sortedTradingPairs?.map((tradePair) => {
              const isInWatchList = currentWatchList.includes(
                tradePair.baseSymbol,
              );
              const isAlphaPair = Number(tradePair.minHoldingSeconds ?? 0) > 0;

              return (
                <MassiveTokenPairItem
                  key={tradePair.address}
                  tradePair={tradePair}
                  onClick={() => {
                    setTradingPair(tradePair);
                    if (isAlphaPair && !hasSeenAlphaMarketIntro()) {
                      pendingItemClickCbRef.current =
                        onItemClickCallBack ?? null;
                      setIsAlphaIntroOpen(true);
                      
return;
                    }
                    onItemClickCallBack?.();
                  }}
                  isInWatchList={isInWatchList}
                  selected={
                    currentTradePairInfo.baseSymbol === tradePair.baseSymbol
                  }
                />
              );
            })}
          </Table.Body>
        </Table.Root>
      </Box>

      <AlphaMarketIntroModal
        isOpen={isAlphaIntroOpen}
        onClose={() => {
          pendingItemClickCbRef.current = null;
          setIsAlphaIntroOpen(false);
        }}
        onConfirm={() => {
          markAlphaMarketIntroSeen();
          setIsAlphaIntroOpen(false);
          pendingItemClickCbRef.current?.();
          pendingItemClickCbRef.current = null;
        }}
      />
      {sortedTradingPairs?.length === 0 && (
        <Flex
          mb={2}
          w="full"
          justifyContent="center"
          alignItems="center"
          flex={1}
        >
          <Text textStyle="text.small" color="greyWhite.lightGrey4">
            {t('common.No trading pair found')}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

const itemCommonStyle = css.raw({
  bg: 'none',
  w: 'full',
  h: 'full',
  py: '4px',
});

const MassiveTokenPairItem = ({
  tradePair,
  onClick,
  isInWatchList,
  selected,
}: {
  tradePair: TradePair;
  onClick: () => void;
  isInWatchList: boolean;
  selected: boolean;
}) => {
  const { t } = useTranslation();

  const price = usePriceLive(tradePair.baseSymbol);
  const [shouldFetch, setShouldFetch] = useState(false);
  const { intersectionRef } = useIntersectionObserver<HTMLTableRowElement>({
    onIntersect: () => {
      setShouldFetch(true);
    },
    rootMargin: '0px 0px 100px',
  });

  const change24h = usePair24hAgoPrice({
    pairBase: tradePair.address,
    pythId: tradePair.pythPriceFeedId,
    currentPrice: price?.toNumber() ?? undefined,
    enabled: shouldFetch,
  });
  const { data: vol24hInBaseSymbol } = useTradePairsVolumeByAddresses({
    pairBaseAddresses: [tradePair.address],
    range: GetVolumeRangeEnum.OneDay,
  });

  // useTradePairsVolumeByAddresses returns an array, we need to find the one that matches our baseSymbol
  const volumeData = vol24hInBaseSymbol?.find(
    (v) => v.symbol === tradePair.baseSymbol,
  );

  const priceDisplayDecimals = useTokenDisplayDecimals(
    tradePair.baseSymbol,
    tradePair.priceDisplayDecimals,
  );
  const isTradePairSupportDegen = useIsTradePairSupportDegen(tradePair);
  const maxLeverage = getPairMaxLeverage({ tradePair });

  // align behavior with top funding rate, change the rate displaying when leverage change
  const tradingPair = useTradingPair((v) => v.useTradingPair());
  const { longFundingRatePerHour, shortFundingRatePerHour } =
    useLongShortFundingRatePerHour({
      tradePair:
        tradingPair.baseSymbol === tradePair.baseSymbol // base symbol same, but still can be normal and degen diff, once base symbol the same, show the current user select pair 's funding rate
          ? tradingPair
          : tradePair,
    });

  const { longRatioInSum, longOIUsd, shortRatioInSum, shortOIUsd } =
    useOpenInterest(tradePair);

  const addToWatchList = useTradePairWatchListStore((s) => s.addToWatchList);
  const removeFromWatchList = useTradePairWatchListStore(
    (s) => s.removeFromWatchList,
  );

  const handleWatchlistClick = (
    e: React.MouseEvent,
    action: 'add' | 'remove',
  ) => {
    e.stopPropagation(); // Prevent event from bubbling up to the row
    if (action === 'add') {
      addToWatchList(tradePair.baseSymbol);
    } else {
      removeFromWatchList(tradePair.baseSymbol);
    }
  };

  const isPairSleeping = useIsPairClose(tradePair);

  return (
    <Table.Row
      ref={intersectionRef}
      key={tradePair.address}
      onClick={onClick}
      _hover={{
        cursor: 'pointer',
        background: 'v2.bg.surface1',
      }}
      boxShadow={selected ? 'inset 2px 0 0 var(--colors-v2-long-default)' : 'none'}
      borderBottom="1px solid"
      borderBottomColor="v2.border.hairline"
      transition="background 120ms" 
      data-cy={`TradePair-${tradePair.pairName}`}
      data-cy-id={tradePair.address}
    >
      <Table.Cell
        css={css.raw({
          borderLeftColor: {
            // base: 'black.lightBlack',
            // desktop: selected ? 'primary.orange' : 'black.lightBlack',
          },
        })}
      >
        <HStack
          borderLeftRadius="8px"
          pl="12px"
          css={itemCommonStyle}
          gap={1}
          minH="40px"
        >
          {isInWatchList ? (
            <Button
              variant="text"
              color="v2.warning"
              onClick={(e) => handleWatchlistClick(e, 'remove')}
            >
              <Icon boxSize="14px">
                <Star />
              </Icon>
            </Button>
          ) : (
            <Button
              variant="text"
              color="v2.text.disabled"
              _hover={{ color: 'v2.text.secondary' }}
              onClick={(e) => handleWatchlistClick(e, 'add')}
            >
              <Icon boxSize="14px">
                <StarOutline />
              </Icon>
            </Button>
          )}
        </HStack>
      </Table.Cell>
      <Table.Cell
        css={css.raw({
          borderLeftColor: {
            // base: 'black.lightBlack',
            // desktop: selected ? 'primary.orange' : 'black.lightBlack',
          },
        })}
      >
        <HStack
          borderLeftRadius="8px"
          pl="12px"
          pr="12px"
          css={itemCommonStyle}
          gap={1}
          minH="40px"
        >
          <styled.img src={tradePair.image} boxSize="18px" borderRadius="full" />
          <HStack ml={1} gap={1} alignItems="flex-start">
            <Text
              textStyle={{
                base: 'headings.small',
              }}
              style={{
                // @ts-expect-error dynamic font size for hard-coded container to avoid overflowing
                '--content-length': tradePair.baseSymbol.length,
              }}
              fontSize="clamp(10px, calc(90px / var(--content-length)), 13px)"
            >
              {tradePair.name}
            </Text>
            <HStack gap={1}>
              <LeverageBadge
                leverage={
                  isTradePairSupportDegen
                    ? new BigNumber(1001)
                    : new BigNumber(maxLeverage || 100)
                }
                isDegen={tradePair.isDegenPair ?? isTradePairSupportDegen}
              />
              {/* {isPairSleeping && <SleepIndicator />} */}
              <MarketTimeIndicator pairName={tradePair.pairName} />
              {Number(tradePair.minHoldingSeconds ?? 0) > 0 && (
                <TextTooltip
                  contentCss={{
                    w: '280px',
                  }}
                  content={
                    <VStack gap={2.5} alignItems="flex-start">
                      <HStack gap={2} alignItems="center">
                        {/* <Badge
                          textStyle="text.xxxxsmall"
                          size="sm"
                          variant="outline"
                          bg="rgba(0, 200, 255, 0.10)"
                          color="#7DEBFF"
                          borderColor="rgba(0, 200, 255, 0.55)"
                          borderWidth="1px"
                          borderRadius="full"
                          px="6px"
                          py="2px"
                        >
                          Alpha
                        </Badge> */}
                        <VStack gap={0.5} alignItems="flex-start">
                          <Text textStyle="text.small" fontWeight={700}>
                            Alpha Market
                          </Text>
                          <Text
                            textStyle="text.xsmall"
                            color="greyWhite.lightGrey2"
                            lineHeight="1.2"
                          >
                            Extra constraints &amp; higher risk profile
                          </Text>
                        </VStack>
                      </HStack>

                      <Box
                        w="full"
                        h="1px"
                        bg="rgba(255, 255, 255, 0.08)"
                      />

                      <VStack gap={1.5} alignItems="flex-start">
                        {[
                          'Higher risk market',
                          'Lower OI cap & leverage',
                          'No TP during minimum trade duration',
                          'Minimum trade duration based on position size',
                        ].map((line) => (
                          <HStack key={line} gap={2} alignItems="flex-start">
                            <Box
                              mt="5px"
                              boxSize="6px"
                              borderRadius="full"
                              bg="v2.text.muted"
                              flexShrink={0}
                            />
                            <Text textStyle="text.small" lineHeight="1.35">
                              {line}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </VStack>
                  }
                  positioning={{ placement: 'top' }}
                >
                  <Badge
                    textStyle="text.xxxxsmall"
                    size="sm"
                    variant="outline"
                    bg="rgba(245, 197, 24, 0.08)"
                    color="v2.warning"
                    borderColor="rgba(245, 197, 24, 0.35)"
                    borderWidth="1px"
                    borderRadius="2px"
                    px="4px"
                    py="2px"
                    cursor="help"
                  >
                    Alpha
                  </Badge>
                </TextTooltip>
              )}
            </HStack>
          </HStack>
        </HStack>
      </Table.Cell>
      <Table.Cell>
        <VStack
          textStyle="text.small"
          alignItems="flex-start"
          gap={1}
          css={itemCommonStyle}
          justifyContent="space-between"
        >
          <AmountUsd
            textStyle={{
              base: 'numbers.small',
            }}
            color="v2.text.primary"
            value={price}
            opts={{
              displayDecimals: priceDisplayDecimals,
            }}
            h="full"
            display="flex"
            alignItems="center"
            data-cy={`Price-${tradePair.pairName}`}
            data-cy-value={price}
          />
        </VStack>
      </Table.Cell>
      <Table.Cell>
        <VStack
          textStyle="text.small"
          alignItems="flex-start"
          gap={1}
          // css={itemCommonStyle}
          justifyContent="space-between"
        >
          {change24h?.rate && (
            <Percentage
              textStyle={{
                base: 'text.small',
              }}
              value={change24h?.rate}
              opts={{
                isShowSign: true,
              }}
              data-cy={`PriceChange-${tradePair.pairName}`}
              data-cy-value={change24h?.rate}
            />
          )}
        </VStack>
      </Table.Cell>
      <Table.Cell>
        <VStack
          textStyle="text.small"
          alignItems="flex-start"
          gap={0}
          justifyContent="space-between"
        >
          <StatAmountUsd
            value={volumeData?.volumeUSD}
            opts={{
              kmb: true,
              displayDecimals: 2,
            }}
            // fontWeight="bold"
            data-cy={`24H-VolumeUSD-${tradingPair?.pairName}`}
            data-cy-value={volumeData?.volumeUSD}
          />
        </VStack>
      </Table.Cell>
      <Table.Cell>
        <HStack textStyle="text.small" gap={0}>
          <HStack gap={0} alignItems="center" justifyContent="flex-start">
            <Percentage
              value={longFundingRatePerHour}
              color={getSemanticPropsByNumber(longFundingRatePerHour).color}
              opts={{
                isShowSign: true,
                displayDecimals: FUNDING_RATE_DISPLAY_DECIMALS,
              }}
              textStyle="text.small"
              data-cy={`LongFundingRatePerHour-${tradePair.pairName}`}
              data-cy-value={longFundingRatePerHour}
            />
          </HStack>
          <Text>/</Text>
          <HStack gap={0} alignItems="center" justifyContent="flex-start">
            <Percentage
              value={shortFundingRatePerHour}
              color={getSemanticPropsByNumber(shortFundingRatePerHour).color}
              opts={{
                isShowSign: true,
                displayDecimals: FUNDING_RATE_DISPLAY_DECIMALS,
              }}
              textStyle="text.small"
              data-cy={`ShortFundingRatePerHour-${tradePair.pairName}`}
              data-cy-value={shortFundingRatePerHour}
            />
          </HStack>
        </HStack>
      </Table.Cell>
      <Table.Cell>
        <HStack
          textStyle="text.small"
          gap={0}
          pr="12px"
          borderRightRadius="8px"
        >
          <HStack gap={0} alignItems="center" justifyContent="flex-start">
            <StatAmountUsd
              value={longOIUsd}
              opts={{
                kmb: true,
                displayDecimals: 2,
              }}
              // fontWeight="bold"
              data-cy={`LongOpenInterest-${tradePair.pairName}`}
              data-cy-value={longOIUsd}
            />
          </HStack>
          <Text>{' '}/{' '}</Text>
          <HStack gap={0} alignItems="center" justifyContent="flex-start">
            <StatAmountUsd
              value={shortOIUsd}
              opts={{
                kmb: true,
                displayDecimals: 2,
              }}
              // fontWeight="bold"
              data-cy={`ShortOpenInterest-${tradePair.pairName}`}
              data-cy-value={shortOIUsd}
            />
          </HStack>
        </HStack>
      </Table.Cell>
    </Table.Row>
  );
};
